import { useState, useRef, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Toaster } from "@/components/ui/sonner";
import { 
  Upload, Download, Moon, Sun, X, FileText, Loader2, 
  AlertCircle, CheckCircle2, Files, Trash2, GripVertical, Merge,
  Eye, EyeOff, HelpCircle
} from "lucide-react";
import { DndContext, closestCenter, KeyboardSensor, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { arrayMove, SortableContext, sortableKeyboardCoordinates, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import ToolsNav from "../components/ToolsNav";
import { AdContainer, ResponsiveAd, StickyMobileAd } from "../components/AdPlacement";

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Supported file extensions
const SUPPORTED_EXTENSIONS = [
  '.pdf', '.doc', '.docx', '.odt', '.rtf', '.txt',
  '.ppt', '.pptx', '.odp',
  '.xls', '.xlsx', '.ods',
  '.html', '.htm', '.epub'
];

const ACCEPT_STRING = SUPPORTED_EXTENSIONS.join(',');

// Get file type badge color
const getFileColor = (filename) => {
  const ext = filename.split('.').pop().toLowerCase();
  const colors = {
    pdf: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400',
    doc: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400',
    docx: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400',
    ppt: 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400',
    pptx: 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400',
    xls: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400',
    xlsx: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400',
    odt: 'bg-cyan-100 text-cyan-700 dark:bg-cyan-900/30 dark:text-cyan-400',
    odp: 'bg-cyan-100 text-cyan-700 dark:bg-cyan-900/30 dark:text-cyan-400',
    ods: 'bg-cyan-100 text-cyan-700 dark:bg-cyan-900/30 dark:text-cyan-400',
    rtf: 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400',
    txt: 'bg-slate-100 text-slate-700 dark:bg-slate-700 dark:text-slate-300',
    html: 'bg-rose-100 text-rose-700 dark:bg-rose-900/30 dark:text-rose-400',
    htm: 'bg-rose-100 text-rose-700 dark:bg-rose-900/30 dark:text-rose-400',
    epub: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400',
  };
  return colors[ext] || 'bg-slate-100 text-slate-700';
};

// Sortable file item component with page range
function SortableFileItem({ id, file, index, pageRange, onRemove, onPageRangeChange, isProcessing }) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
  } = useSortable({ id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  const ext = file.name.split('.').pop().toUpperCase();

  return (
    <div
      ref={setNodeRef}
      style={style}
      className="flex items-center gap-3 p-3 bg-slate-50 dark:bg-slate-700/50 rounded-lg group"
    >
      <button
        {...attributes}
        {...listeners}
        className="cursor-grab active:cursor-grabbing text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 flex-shrink-0"
        disabled={isProcessing}
      >
        <GripVertical className="w-4 h-4" />
      </button>
      
      <Badge variant="secondary" className={`text-xs font-mono flex-shrink-0 ${getFileColor(file.name)}`}>
        {ext}
      </Badge>
      
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium truncate dark:text-white">{file.name}</p>
        <p className="text-xs text-slate-500">
          {(file.size / 1024).toFixed(1)} KB
        </p>
      </div>
      
      {/* Page Range Input */}
      <div className="flex items-center gap-1 flex-shrink-0">
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <div className="flex items-center gap-1">
                <Input
                  type="text"
                  placeholder="all"
                  value={pageRange}
                  onChange={(e) => onPageRangeChange(id, e.target.value)}
                  disabled={isProcessing}
                  className="w-20 h-7 text-xs"
                />
                <HelpCircle className="w-3 h-3 text-slate-400" />
              </div>
            </TooltipTrigger>
            <TooltipContent side="top" className="max-w-xs">
              <p className="text-xs">
                Page range: e.g., <strong>1-3</strong>, <strong>2,4,6</strong>, <strong>1-3,5,7-10</strong>
                <br />Leave empty or "all" for all pages
              </p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      </div>
      
      <span className="text-xs text-slate-400 flex-shrink-0">#{index + 1}</span>
      
      {!isProcessing && (
        <Button
          size="sm"
          variant="ghost"
          onClick={() => onRemove(id)}
          className="opacity-0 group-hover:opacity-100 text-slate-400 hover:text-red-500 flex-shrink-0"
        >
          <X className="w-4 h-4" />
        </Button>
      )}
    </div>
  );
}

export default function MergeDocsToPdf() {
  const [files, setFiles] = useState([]);
  const [isProcessing, setIsProcessing] = useState(false);
  const [progress, setProgress] = useState(0);
  const [mergedBlob, setMergedBlob] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [showPreview, setShowPreview] = useState(true);
  const [error, setError] = useState("");
  const [isDragging, setIsDragging] = useState(false);
  const [darkMode, setDarkMode] = useState(() => {
    if (typeof window !== 'undefined') {
      const saved = localStorage.getItem('darkMode');
      if (saved !== null) return JSON.parse(saved);
      return window.matchMedia('(prefers-color-scheme: dark)').matches;
    }
    return false;
  });
  const fileInputRef = useRef(null);

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  useEffect(() => {
    if (darkMode) {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
    localStorage.setItem('darkMode', JSON.stringify(darkMode));
  }, [darkMode]);

  useEffect(() => {
    document.title = "Merge Documents to PDF - Combine Files - KayTool";
  }, []);

  // Cleanup preview URL on unmount
  useEffect(() => {
    return () => {
      if (previewUrl) {
        URL.revokeObjectURL(previewUrl);
      }
    };
  }, [previewUrl]);

  const toggleDarkMode = () => setDarkMode(!darkMode);

  const isValidFile = (file) => {
    const ext = '.' + file.name.split('.').pop().toLowerCase();
    return SUPPORTED_EXTENSIONS.includes(ext);
  };

  const handleFileSelect = (e) => {
    const selectedFiles = Array.from(e.target.files || []);
    const validFiles = selectedFiles.filter(isValidFile);
    
    if (validFiles.length === 0) {
      toast.error("No supported files selected");
      return;
    }
    
    if (validFiles.length < selectedFiles.length) {
      toast.warning(`${selectedFiles.length - validFiles.length} unsupported files were skipped`);
    }
    
    const newFiles = validFiles.map(f => ({
      id: `${f.name}-${Date.now()}-${Math.random()}`,
      file: f,
      pageRange: '' // empty means all pages
    }));
    
    setFiles(prev => [...prev, ...newFiles]);
    setMergedBlob(null);
    setPreviewUrl(null);
    setError("");
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    const droppedFiles = Array.from(e.dataTransfer.files);
    const validFiles = droppedFiles.filter(isValidFile);
    
    if (validFiles.length === 0) {
      toast.error("No supported files dropped");
      return;
    }
    
    const newFiles = validFiles.map(f => ({
      id: `${f.name}-${Date.now()}-${Math.random()}`,
      file: f,
      pageRange: ''
    }));
    
    setFiles(prev => [...prev, ...newFiles]);
    setMergedBlob(null);
    setPreviewUrl(null);
    setError("");
  };

  const handleDragEnd = (event) => {
    const { active, over } = event;
    
    if (active.id !== over.id) {
      setFiles((items) => {
        const oldIndex = items.findIndex(i => i.id === active.id);
        const newIndex = items.findIndex(i => i.id === over.id);
        return arrayMove(items, oldIndex, newIndex);
      });
    }
  };

  const handlePageRangeChange = (id, value) => {
    setFiles(prev => prev.map(f => 
      f.id === id ? { ...f, pageRange: value } : f
    ));
  };

  const removeFile = (id) => {
    setFiles(prev => prev.filter(f => f.id !== id));
  };

  const clearAll = () => {
    setFiles([]);
    setMergedBlob(null);
    if (previewUrl) {
      URL.revokeObjectURL(previewUrl);
    }
    setPreviewUrl(null);
    setError("");
    setProgress(0);
  };

  const mergeDocuments = async () => {
    if (files.length === 0) {
      toast.error("Please add files first");
      return;
    }

    setIsProcessing(true);
    setProgress(0);
    setError("");
    setMergedBlob(null);
    if (previewUrl) {
      URL.revokeObjectURL(previewUrl);
    }
    setPreviewUrl(null);

    try {
      setProgress(10);
      
      const formData = new FormData();
      files.forEach(({ file }) => {
        formData.append('files', file);
      });
      
      // Add page ranges as JSON
      const pageRanges = files.map(f => f.pageRange || 'all');
      formData.append('page_ranges', JSON.stringify(pageRanges));
      
      setProgress(30);
      
      const response = await fetch(`${API_URL}/api/merge-docs/merge`, {
        method: 'POST',
        body: formData,
      });
      
      setProgress(70);

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Merge failed');
      }

      const blob = await response.blob();
      setMergedBlob(blob);
      
      // Create preview URL
      const url = URL.createObjectURL(blob);
      setPreviewUrl(url);
      
      setProgress(100);

      toast.success(`Merged ${files.length} documents into PDF!`);
    } catch (error) {
      console.error("Merge error:", error);
      setError(error.message);
      toast.error("Merge failed: " + error.message);
    }

    setIsProcessing(false);
  };

  const downloadMerged = () => {
    if (!mergedBlob) return;
    const url = URL.createObjectURL(mergedBlob);
    const a = document.createElement('a');
    a.href = url;
    a.download = files.length === 1 
      ? files[0].file.name.replace(/\.[^/.]+$/, '.pdf')
      : `merged_${files.length}_documents.pdf`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    toast.success("Merged PDF downloaded!");
  };

  const formatSize = (bytes) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
  };

  const totalSize = files.reduce((sum, f) => sum + f.file.size, 0);

  return (
    <div className={`min-h-screen transition-colors duration-300 ${darkMode ? 'dark bg-slate-900' : 'bg-gradient-to-br from-violet-50 to-purple-50'}`}>
      <Toaster position="top-center" richColors />
      
      <header className="border-b border-slate-200 dark:border-slate-700 bg-white/80 dark:bg-slate-800/80 backdrop-blur-sm sticky top-0 z-50">
        <div className="container mx-auto px-4 py-4 flex items-center justify-between">
          <a href="/" className="flex items-center gap-2">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-violet-500 to-purple-600 flex items-center justify-center">
              <Merge className="w-6 h-6 text-white" />
            </div>
            <span className="text-xl font-bold text-slate-800 dark:text-white">KayTool</span>
          </a>
          <div className="flex items-center gap-4">
            <ToolsNav />
            <Badge variant="outline" className="hidden sm:flex gap-1 dark:text-slate-300">
              <Files className="w-3 h-3" /> Multi-Format
            </Badge>
            <Button variant="ghost" size="icon" onClick={toggleDarkMode} className="dark:text-slate-300">
              {darkMode ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
            </Button>
          </div>
        </div>
      </header>

      <AdContainer className="mt-4 mb-0">
        <ResponsiveAd slot="above-fold-header" mobileSize="mobile-banner" desktopSize="banner" />
      </AdContainer>

      <main className="container mx-auto px-4 py-8">
        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-violet-500 to-purple-600 text-white rounded-full mb-4">
            <Merge className="w-5 h-5" />
            <span className="font-medium">Merge Documents</span>
          </div>
          <h1 className="text-4xl font-bold text-slate-800 dark:text-white mb-4">
            Merge Documents to PDF
          </h1>
          <p className="text-lg text-slate-600 dark:text-slate-400 max-w-2xl mx-auto">
            Combine Word, Excel, PowerPoint, and other documents into a single PDF. 
            Drag to reorder and set page ranges for each file.
          </p>
        </div>

        <div className="max-w-4xl mx-auto space-y-6">
          {/* Upload Area */}
          <Card 
            className={`border-2 border-dashed transition-all duration-300 dark:bg-slate-800 ${
              isDragging ? 'border-violet-500 bg-violet-50 dark:bg-violet-900/20' : 'border-slate-300 dark:border-slate-600'
            }`}
            onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
            onDragLeave={() => setIsDragging(false)}
            onDrop={handleDrop}
          >
            <CardContent className="p-8 text-center">
              <input
                ref={fileInputRef}
                type="file"
                accept={ACCEPT_STRING}
                multiple
                onChange={handleFileSelect}
                className="hidden"
                data-testid="docs-file-input"
              />
              <div 
                className="cursor-pointer"
                onClick={() => fileInputRef.current?.click()}
                data-testid="upload-zone"
              >
                <Upload className="w-12 h-12 mx-auto mb-4 text-violet-500" />
                <h3 className="text-xl font-semibold text-slate-700 dark:text-white mb-2">
                  Drop documents or click to upload
                </h3>
                <p className="text-slate-500 dark:text-slate-400 mb-3">
                  PDF, Word, Excel, PowerPoint, and more
                </p>
                <div className="flex flex-wrap gap-2 justify-center">
                  {['PDF', 'DOCX', 'XLSX', 'PPTX', 'ODT', 'RTF', 'TXT'].map(ext => (
                    <Badge key={ext} variant="secondary" className="text-xs">
                      {ext}
                    </Badge>
                  ))}
                </div>
              </div>
            </CardContent>
          </Card>

          {/* File List with Drag & Drop Reordering */}
          {files.length > 0 && (
            <Card className="dark:bg-slate-800">
              <CardContent className="p-4">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <h3 className="font-semibold dark:text-white flex items-center gap-2">
                      <Files className="w-5 h-5 text-violet-500" />
                      {files.length} document{files.length > 1 ? 's' : ''} 
                      <span className="text-sm font-normal text-slate-500">
                        ({formatSize(totalSize)})
                      </span>
                    </h3>
                    <p className="text-xs text-slate-500 mt-1">
                      Drag to reorder • Set page ranges (e.g., 1-3, 5, 7-10)
                    </p>
                  </div>
                  <Button variant="ghost" size="sm" onClick={clearAll} className="text-red-500 hover:text-red-600">
                    <Trash2 className="w-4 h-4 mr-1" /> Clear All
                  </Button>
                </div>
                
                {/* Progress Bar */}
                {isProcessing && (
                  <div className="mb-4">
                    <div className="flex items-center gap-4 mb-2">
                      <Loader2 className="w-5 h-5 animate-spin text-violet-500" />
                      <span className="text-sm text-slate-600 dark:text-slate-300">
                        Merging documents...
                      </span>
                    </div>
                    <Progress value={progress} className="h-2" />
                  </div>
                )}
                
                {/* Sortable File List */}
                <DndContext
                  sensors={sensors}
                  collisionDetection={closestCenter}
                  onDragEnd={handleDragEnd}
                >
                  <SortableContext
                    items={files.map(f => f.id)}
                    strategy={verticalListSortingStrategy}
                  >
                    <div className="space-y-2 max-h-64 overflow-y-auto">
                      {files.map((fileItem, index) => (
                        <SortableFileItem
                          key={fileItem.id}
                          id={fileItem.id}
                          file={fileItem.file}
                          index={index}
                          pageRange={fileItem.pageRange}
                          onRemove={removeFile}
                          onPageRangeChange={handlePageRangeChange}
                          isProcessing={isProcessing}
                        />
                      ))}
                    </div>
                  </SortableContext>
                </DndContext>
              </CardContent>
            </Card>
          )}

          {/* Error */}
          {error && (
            <Card className="dark:bg-slate-800 border-red-200 dark:border-red-800">
              <CardContent className="p-4">
                <div className="flex items-start gap-2">
                  <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
                  <p className="text-sm text-red-600 dark:text-red-300">{error}</p>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Merge Button */}
          {files.length > 0 && !mergedBlob && !isProcessing && (
            <div className="text-center">
              <Button 
                onClick={mergeDocuments}
                size="lg"
                className="bg-gradient-to-r from-violet-500 to-purple-600 hover:opacity-90"
                data-testid="merge-btn"
              >
                <Merge className="w-5 h-5 mr-2" />
                Merge {files.length} Document{files.length > 1 ? 's' : ''} to PDF
              </Button>
            </div>
          )}

          {/* Success with Preview */}
          {mergedBlob && (
            <Card className="dark:bg-slate-800">
              <CardContent className="p-6">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 rounded-full bg-green-100 dark:bg-green-900/30 flex items-center justify-center">
                      <CheckCircle2 className="w-6 h-6 text-green-600 dark:text-green-400" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-slate-800 dark:text-white">
                        Documents Merged Successfully!
                      </h3>
                      <p className="text-sm text-slate-500 dark:text-slate-400">
                        {files.length} document{files.length > 1 ? 's' : ''} combined • {formatSize(mergedBlob.size)}
                      </p>
                    </div>
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setShowPreview(!showPreview)}
                    className="flex items-center gap-1"
                  >
                    {showPreview ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    {showPreview ? 'Hide' : 'Show'} Preview
                  </Button>
                </div>

                {/* PDF Preview */}
                {showPreview && previewUrl && (
                  <div className="mb-4 rounded-lg overflow-hidden border border-slate-200 dark:border-slate-600">
                    <iframe
                      src={previewUrl}
                      className="w-full h-96 bg-slate-100 dark:bg-slate-700"
                      title="PDF Preview"
                    />
                  </div>
                )}

                <div className="flex flex-col sm:flex-row gap-3 justify-center">
                  <Button 
                    onClick={downloadMerged}
                    className="bg-gradient-to-r from-green-500 to-green-600"
                    data-testid="download-merged-btn"
                  >
                    <Download className="w-4 h-4 mr-2" />
                    Download Merged PDF
                  </Button>
                  <Button variant="outline" onClick={clearAll}>
                    Merge More Documents
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}
        </div>

        <AdContainer className="mt-8">
          <ResponsiveAd slot="after-features" mobileSize="mobile-banner" desktopSize="banner" />
        </AdContainer>
      </main>

      <footer className="border-t border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 py-8 mt-12">
        <div className="container mx-auto px-4 text-center text-sm text-slate-600 dark:text-slate-400">
          <p>&copy; {new Date().getFullYear()} KayTool. Secure document merging.</p>
        </div>
      </footer>

      <StickyMobileAd slot="sticky-footer" />
    </div>
  );
}
